// React and hooks
import React, { useCallback, useEffect, useRef, useState } from 'react'

// Third-party libraries
import moment from 'moment'
import { Col, Modal, Row } from 'react-bootstrap'

// Custom components
import { Button, ConfirmationModal, Stepper } from '@components/common'
import Scheduling, { AvailableStartTimes } from './Scheduling.component'
import Forms from './Forms.component'
import Review from './Review.component'

// Constants and utilities

// Types and interfaces
import {
  AppointmentRequest,
  AppointmentResponse,
  ILabCases,
  IProcedureLogs,
  PatientWithInsurance,
} from '@dataTypes/appointment.type'
import type { AppointmentNoteOverlayInput } from '@pages/Appointment/appointmentNoteOverlay.util'

// Relative imports
import './Appointment.css'
import { useSelector } from 'react-redux'
import { RootState } from '@store/index'
import Confirmation from './Confirmation.component'
import Plan from './Plan.component'
import { getFullName } from '@utils/fullname.util'
import { v4 as uuid } from 'uuid'
import { INote } from '@dataTypes/note.type'
import { getBundledProcedures } from '@services/api/procedure.api'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createScheduledAppointmentToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { logger } from '@utils/safeLogger'
const toClockTime = (value?: Date | string): string => {
  if (value == null || value === '') return ''
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return ''
  }
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('HH:mm') : ''
}

const getAssignedFormTemplateIdsFromAppt = (
  appt: Partial<AppointmentResponse>,
): string[] => {
  if (appt.form_template_ids?.length) {
    return appt.form_template_ids
  }
  const rows = appt.formAssignments
  if (!rows?.length) {
    return []
  }
  const ids: string[] = []
  for (const row of rows) {
    if (!ids.includes(row.form_template_id)) {
      ids.push(row.form_template_id)
    }
  }
  return ids
}

type IAppointmentModalProps = {
  isOpen: boolean
  handleModal: (isOpen: boolean, appt?: AppointmentResponse) => void
  initalDate?: Date | string
  initialStartTime?: Date | string
  initialEndTime?: Date | string
  initialRoom?: string
  initialProvider?: string
  currentAppt?: Partial<AppointmentResponse> | null
  onSave: (
    appointmentDetails: Partial<AppointmentRequest>,
  ) => Promise<void> | void
  preSelectedPatient?: PatientWithInsurance
  onViewNote?: (input: AppointmentNoteOverlayInput) => void
  /** Passed to Modal `id` (dialog). Default `schedule-appointment-modal` (calendar). */
  modalId?: string
  /** Passed to Modal `backdropClassName` (e.g. `note-overlay-app-modal-backdrop`). */
  backdropClassName?: string
  /** Extra Modal root classes, appended after `custom-modal-width` and step class. */
  customClassName?: string
}

export type AppointmentFormData = {
  patientName: string // patient name
  patientId: string // patient id
  providerName: string // provider name
  providerId: string // provider id
  operatoryName: string // operatory name
  operatoryId: string // operatory id
  duration: number
  apptDate: string // appointment date
  apptStartTime: string //appointment start time
  apptEndTime: string // appointment end time
  appointmentTypeId: string // appointment type
  appointmentTypeName: string // appointment type
  appointmentTypeSubCategoryId: string // appointment type sub
  isRecurring: boolean // is recurring
  recurringVisitId?: string // recurring visit id
  recurringEvery: number // recurring every
  recurringFor: number // recurring for
  recurringDays: string // recurring days
  recurringStartDate: string // recurring start date
  recurringEndDate: string // recurring end date
  isInsured: 'Yes' | 'No' // is insured
  insuranceName?: string // insurance name
  insuranceId?: string // insurance id
  procedures: IProcedureLogs[] | [] // procedures
  tpId?: string | null // treatment plan id
  labOrders?: ILabCases[] | []
  templateName?: string // template name
  templateId?: string // template id
  files: { id?: string; name: string; size: string; url?: string }[] | [] // forms
  notes?: Partial<INote>[]
  contactVia?: string[] // contact via
  insuranceOptions?: PatientWithInsurance['insurance_plans']
  assignedFormIds?: string[] // form builder form IDs added to appointment (Add-ons step)
}

const Appointment: React.FC<IAppointmentModalProps> = ({
  isOpen,
  handleModal,
  currentAppt,
  initialStartTime,
  initialEndTime,
  initialRoom,
  initialProvider,
  onSave,
  preSelectedPatient,
  onViewNote,
  modalId = 'schedule-appointment-modal',
  backdropClassName,
  customClassName,
}) => {
  const clickedStartTime = currentAppt?.appt_start
    ? ''
    : toClockTime(initialStartTime)
  const clickedEndTime = clickedStartTime
    ? moment(clickedStartTime, 'HH:mm')
        .add(currentAppt?.duration ?? 15, 'minutes')
        .format('HH:mm')
    : ''
  const [step, setStep] = useState(0)
  const [skippedSteps, setSkippedSteps] = useState<{ [key: number]: boolean }>(
    {},
  )
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string
  }>({})
  const [availableStartTimes, setAvailableStartTimes] =
    useState<AvailableStartTimes>({ ready: false, times: [] })
  const handleAvailableStartTimesChange = useCallback(
    (availability: AvailableStartTimes) => {
      setAvailableStartTimes(availability)
    },
    [],
  )
  const [formData, setFormData] = useState<AppointmentFormData>({
    patientName: currentAppt?.patient ? getFullName(currentAppt.patient) : '',
    patientId: currentAppt?.patient_id ?? '',
    providerName: currentAppt?.provider
      ? getFullName(currentAppt.provider)
      : '',
    providerId: currentAppt?.provider_id ?? initialProvider ?? '',
    operatoryName: '',
    operatoryId: currentAppt?.operatory_id ?? initialRoom ?? '',
    duration: currentAppt?.duration ?? 15,
    apptDate: moment(currentAppt?.appt_date ?? initialStartTime).format(
      'MM/DD/YYYY',
    ),
    apptStartTime: currentAppt?.appt_start || clickedStartTime,
    apptEndTime: currentAppt?.appt_end || clickedEndTime,
    appointmentTypeId: currentAppt?.appointment_type_id ?? '',
    appointmentTypeName: currentAppt?.appointmenttype?.name ?? '',
    appointmentTypeSubCategoryId:
      currentAppt?.appointment_type_subcategory_id ?? '',
    isRecurring: false,
    recurringVisitId: currentAppt?.recurring_visit_id ?? '',
    recurringEvery: currentAppt?.recurring_every ?? 0,
    recurringFor: currentAppt?.recurring_for ?? 1,
    recurringDays: currentAppt?.recurring_days ?? '0,1,2,3,4',
    recurringStartDate: currentAppt?.recurring_start_date
      ? moment(currentAppt.recurring_start_date).format('MM/DD/YYYY')
      : moment(initialStartTime).format('MM/DD/YYYY'),
    recurringEndDate: currentAppt?.recurring_end_date
      ? moment(currentAppt.recurring_end_date).format('MM/DD/YYYY')
      : moment(initialStartTime).add(7, 'days').format('MM/DD/YYYY'),
    isInsured: currentAppt?.is_insured === true ? 'Yes' : 'No',
    procedures: currentAppt?.procedure_logs ?? [],
    insuranceName: '',
    insuranceId: currentAppt?.insurance_plan_id ?? '',
    labOrders: currentAppt?.lab_orders ?? [],
    templateName: currentAppt?.template?.template_name ?? '',
    templateId: currentAppt?.template?.template_id ?? '',
    files: currentAppt?.files ?? [],
    notes: currentAppt?.notes ?? [],
    contactVia: currentAppt?.contact_via ?? [],
    assignedFormIds: [],
  })

  useEffect(() => {
    if (!availableStartTimes.ready || !formData.apptStartTime) return
    const selectedTime = moment(
      formData.apptStartTime,
      ['HH:mm:ss', 'HH:mm'],
      true,
    ).format('HH:mm')
    if (!availableStartTimes.times.includes(selectedTime)) return
    setValidationErrors((prev) => {
      if (prev.apptStartTime !== 'Selected time is not available') return prev
      const next = { ...prev }
      delete next.apptStartTime
      return next
    })
  }, [availableStartTimes, formData.apptStartTime])

  const [invalidFileAlert, setInvalidFileAlert] = useState({
    show: false,
    fileName: '',
  })

  /**
   * When the effect re-runs for the same appointment (e.g. initialStartTime changed),
   * keep in-modal notes from overlay sync instead of resetting to stale currentAppt.notes.
   */
  const apptNotesIdentityRef = useRef<string | '__UNSET__'>('__UNSET__')

  const steps = ['Scheduling', 'Plan', 'Add-ons', 'Review']
  const { loading, error } = useSelector(
    (state: RootState) => state.appointment,
  )
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()

  const user = JSON.parse(localStorage.getItem('user') || '{}')

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {}

    if (!formData.patientId) {
      errors.patientId = 'Select a patient'
    }

    if (!formData.providerId) {
      errors.providerId = 'Select a provider'
    }

    if (!formData.appointmentTypeId) {
      errors.appointmentTypeId = 'Select an appointment type'
    }

    if (
      formData.appointmentTypeId !== '' &&
      !formData.appointmentTypeSubCategoryId
    ) {
      errors.appointmentTypeSubCategoryId = 'Select a sub category'
    }

    if (!formData.apptDate) {
      errors.apptDate = 'Select a date'
    }

    if (!formData.duration) {
      errors.duration = 'Select a duration'
    }

    if (!formData.apptStartTime || !formData.apptEndTime) {
      errors.apptStartTime = 'Select a time'
    } else if (
      availableStartTimes.ready &&
      !availableStartTimes.times.includes(
        moment(formData.apptStartTime, ['HH:mm:ss', 'HH:mm'], true).format(
          'HH:mm',
        ),
      )
    ) {
      errors.apptStartTime = 'Selected time is not available'
    }

    if (!formData.operatoryId) {
      errors.operatoryId = 'Select a room'
    }

    if (formData.isInsured === 'Yes' && !formData.insuranceId) {
      errors.insuranceId = 'Select insurance'
    }

    setValidationErrors(errors)

    return Object.keys(errors).length === 0
  }

  const handleNext = (): void => {
    if (validateForm()) {
      // Remove current step from skippedSteps if it was previously skipped
      setSkippedSteps((prev) => {
        const updated = { ...prev }
        if (updated[step]) {
          delete updated[step]
        }
        return updated
      })

      setStep(step + 1)
    }
  }

  const handlePrevious = (): void => setStep(step - 1)

  const handleEditButton = (): void => setStep(0)

  const onModalClose = (): void => {
    setStep(0)
    setValidationErrors({})
    handleModal(false)
  }

  // Sanitize and prepare the procedure_logs
  const preparedProcedures = (formData.procedures ?? [])
    .filter((proc) => !(proc.source === 2 && proc.parentId === null))
    .map((proc) => {
      const baseFields = {
        proc_log_id: proc.proc_log_id,
        area_type: proc.area_type,
        area: proc.area,
        tooth: proc.tooth,
        surf: proc.surf,
        tooth_details: proc.tooth_details ?? [],
        insurance_plan_id: proc.insurance_plan_id || null,
        clinic_fee: proc.clinic_fee || null,
        allowed_amount: proc.allowed_amount || null,
        write_off: proc.write_off || null,
        estimated_insurance_pay: proc.estimated_insurance_pay || null,
        estimated_patient_resp: proc.estimated_patient_resp || null,
      }

      if (proc.proc_log_id) {
        // Existing → send only what's needed
        return baseFields
      }

      // New → include required fields
      return {
        ...baseFields,
        proc_id: proc.proc_id,
        proc_code: proc.proc_code,
        provider_id: proc.provider_id,
        patient_id: proc.patient_id,
        proc_date: proc.proc_date,
        proc_status: proc.proc_status ?? 0,
        priority: proc.priority ?? 0,
        proc_description: proc.proc_description,
        ...(proc.source !== undefined && { source: proc.source }),
        ...(proc.source_id !== undefined && { source_id: proc.source_id }),
      }
    })

  const preparedLabOrders = (formData.labOrders ?? []).map((lab) => {
    const baseFields = {
      lab_case_id: lab.lab_case_id,
    }

    // If it's an existing lab order (lab_case_id only)
    if (
      Object.keys(lab).length === 1 || // only lab_case_id present
      (!lab.patient_id && !lab.instructions && !lab.invoice_num)
    ) {
      return baseFields
    }

    // New lab case or full update
    return baseFields
  })

  const handleSave = async (): Promise<void> => {
    showLoading({
      container: '.appointment-modal',
      hideElements: ['.calendar-container'],
    })
    let appointmentDetails: Partial<AppointmentRequest> = {
      patient_id: formData.patientId,
      provider_id: formData.providerId,
      operatory_id: formData.operatoryId,
      appointment_type_id: formData.appointmentTypeId,
      appointment_type_subcategory_id: formData.appointmentTypeSubCategoryId,
      appt_status: currentAppt?.appt_status || 9,
      appt_date: moment(formData.apptDate).format('YYYY-MM-DD'),
      appt_start: formData.apptStartTime,
      appt_end: formData.apptEndTime,
      duration: formData.duration,
      is_recurring: formData.isRecurring,
      recurring_visit_id: currentAppt?.recurring_visit_id || uuid(),
      recurring_every: formData.recurringEvery,
      recurring_for: formData.recurringFor,
      recurring_days: formData.recurringDays,
      recurring_start_date: moment(formData.recurringStartDate).format(
        'YYYY-MM-DD',
      ),
      recurring_end_date: moment(formData.recurringEndDate).format(
        'YYYY-MM-DD',
      ),
      tp_id: formData.tpId || null,
      files: formData.files ?? [],
      form_template_ids: formData.assignedFormIds ?? [],
      template_id: formData.templateId || null,
      lab_order: preparedLabOrders ?? [],
      procedure_logs: preparedProcedures ?? [],
      insurance_plan_id: formData.insuranceId || null,
      is_insured: formData.isInsured === 'Yes' ? true : false,
      notes: formData.notes ?? [],
      payment_status: 0,
      contact_via: formData.contactVia ?? [],
      created_by: user.user_id,
      updated_by: user.user_id,
    }

    // If updating an existing appointment
    if (currentAppt?.appointment_id) {
      appointmentDetails = {
        ...appointmentDetails,
        appointment_id: currentAppt.appointment_id,
      }
    }

    try {
      await onSave(appointmentDetails)
      setTimeout(() => {
        hideLoading('.appointment-modal')
        onModalClose()
        showToast(
          currentAppt?.appointment_id
            ? createSuccessToast(
                'Appointment saved successfully',
                'Appointment has been saved successfully.',
              )
            : createScheduledAppointmentToast(
                formData.apptDate,
                formData.apptStartTime,
              ),
        )
      }, 2000)
    } catch (e) {
      logger.error('Error saving appointment:', e)
      setTimeout(() => {
        hideLoading('.appointment-modal')
        showToast(
          createFailedToast(
            'Appointment not saved',
            'Appointment not saved. Please try again.',
          ),
        )
      }, 2000)
    }
  }

  const handleChange = (
    field: string,
    value:
      | string
      | Date
      | number
      | boolean
      | Partial<IProcedureLogs>[]
      | Partial<INote>[]
      | string[]
      | null,
  ): void => {
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [field]: value,
      }

      if (field === 'appointmentTypeId') {
        newFormData.appointmentTypeSubCategoryId = ''
      }

      if (field === 'duration' && newFormData.apptStartTime) {
        const durationMinutes = Number(value)
        const start = moment(
          newFormData.apptStartTime,
          ['HH:mm:ss', 'HH:mm'],
          true,
        )
        if (durationMinutes > 0 && start.isValid()) {
          newFormData.apptEndTime = start
            .add(durationMinutes, 'minutes')
            .format('HH:mm')
        }
      }

      if (field === 'isInsured' && value === 'No') {
        newFormData.insuranceId = ''
        newFormData.insuranceName = ''
      }

      if (field === 'providerId') {
        newFormData.operatoryId = ''
        newFormData.operatoryName = ''
      }

      return newFormData
    })

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors }
      if (value) {
        delete updatedErrors[field]
      }
      return updatedErrors
    })
  }

  const handleFileUpload = (files: FileList | null): void => {
    if (files) {
      const validExtensions = ['docx', 'pdf']
      const fileArray = Array.from(files)
        .filter((file) => {
          const extension = file.name.split('.').pop()?.toLowerCase()
          if (!validExtensions.includes(extension || '')) {
            setInvalidFileAlert({
              show: true,
              fileName: file.name,
            })
            return false
          }
          return true
        })
        .map((file) => {
          let size = file.size / 1024 // Size in KB
          let sizeUnit = 'KB'

          // Convert to MB if the size exceeds 1024 KB
          if (size > 1024) {
            size = size / 1024
            sizeUnit = 'MB'
          }

          return {
            name: file.name,
            size: `${size.toFixed(2)} ${sizeUnit}`, // Format to 2 decimal places
          }
        })

      setFormData((prevState) => ({
        ...prevState,
        files: [...(prevState.files || []), ...fileArray], // Ensuring files are added correctly
      }))
    }
  }

  const handleDeleteFile = (index: number): void => {
    setFormData((prevState) => {
      const updatedFiles = [...prevState.files]
      updatedFiles.splice(index, 1)
      return { ...prevState, files: updatedFiles }
    })
  }

  const handleOverlayNotesSync = (notes: Partial<INote>[]): void => {
    handleChange('notes', notes)
  }

  const mergeAppointmentSavedNote = useCallback((saved: INote): void => {
    setFormData((prev) => {
      const list = prev.notes ?? []
      const id = saved.note_id
      const idx = list.findIndex(
        (n) => n.note_id != null && String(n.note_id) === String(id),
      )
      if (idx >= 0) {
        const next = [...list]
        next[idx] = { ...next[idx], ...saved }
        return { ...prev, notes: next }
      }
      return { ...prev, notes: [...list, saved] }
    })
  }, [])

  const handleOpenViewNote = (input: AppointmentNoteOverlayInput): void => {
    if (!onViewNote) return
    const childNotesSync = input.onNotesSync
    onViewNote({
      ...input,
      onNotesSync: (notes) => {
        if (childNotesSync) {
          childNotesSync(notes)
        } else {
          handleOverlayNotesSync(notes)
        }
      },
    })
  }

  const getSectionComponent = (): React.ReactElement => {
    switch (step) {
      case 0:
        return (
          <Scheduling
            formData={formData}
            handleChange={handleChange}
            validationErrors={validationErrors}
            onAvailableStartTimesChange={handleAvailableStartTimesChange}
            {...(preSelectedPatient && {
              preSelectedPatient,
            })}
            {...(currentAppt?.appointment_id && {
              currentApptId: currentAppt.appointment_id,
            })}
          />
        )
      case 1:
        return <Plan formData={formData} handleChange={handleChange} />
      case 2:
        return (
          <Forms
            formData={formData}
            handleChange={handleChange}
            handleDeleteFile={handleDeleteFile}
            handleFileUpload={handleFileUpload}
            onViewNote={handleOpenViewNote}
            appointmentId={currentAppt?.appointment_id}
            mergeAppointmentSavedNote={mergeAppointmentSavedNote}
          />
        )
      case 3:
        return (
          <Review
            formData={formData}
            handleEditButton={handleEditButton}
            handleChange={handleChange}
            skippedSteps={skippedSteps}
            onViewNote={handleOpenViewNote}
            mergeAppointmentSavedNote={mergeAppointmentSavedNote}
            {...(currentAppt?.appointment_id && {
              appointmentId: currentAppt.appointment_id,
            })}
          />
        )
      case 4:
        return <Confirmation />
      default:
        return <div></div>
    }
  }

  const [skipConfirmationModal, setSkipConfirmationModal] = useState({
    show: false,
    title: 'Clear data',
    body: (
      <div className="confirmation-modal__body">
        <p className="confirmation-modal__title">Clear entered data?</p>
        <p className="confirmation-modal__message">
          Skipping this step will erase the entered data. Are you sure you want
          to proceed?
        </p>
      </div>
    ),
    footer: (currentStep: number) => {
      return (
        <div className="confirmation-modal__footer">
          <Button
            className="button-secondary"
            size="md"
            onClick={() => {
              setSkipConfirmationModal((prev) => ({ ...prev, show: false }))
            }}
          >
            Cancel
          </Button>
          <Button
            className="button-destructive-primary"
            size="md"
            onClick={() => {
              switch (currentStep) {
                case 1:
                  handleChange('procedures', [])
                  break
                case 2:
                  handleChange('labOrders', [])
                  handleChange('notes', [])
                  handleChange('templateId', '')
                  handleChange('files', [])
                  handleChange('assignedFormIds', [])
                  break
              }
              setSkipConfirmationModal((prev) => ({ ...prev, show: false }))
              setSkippedSteps((prev) => ({
                ...prev,
                [currentStep]: true,
              }))
              setStep(currentStep + 1)
            }}
          >
            Proceed
          </Button>
        </div>
      )
    },
  })

  const handleSkip = (): void => {
    switch (step) {
      case 1:
        if (formData.procedures.length <= 0) {
          setSkippedSteps((prev) => ({ ...prev, [step]: true }))
          setStep(step + 1)
          return
        }
        break
      case 2:
        if (
          (formData.templateId === undefined ||
            formData.templateId === null ||
            formData.templateId === '') &&
          (formData.labOrders === undefined ||
            formData.labOrders === null ||
            formData.labOrders.length <= 0) &&
          (formData.notes === undefined ||
            formData.notes === null ||
            formData.notes.length <= 0) &&
          (formData.files === undefined ||
            formData.files === null ||
            formData.files.length <= 0)
        ) {
          setSkippedSteps((prev) => ({ ...prev, [step]: true }))
          setStep(step + 1)
          return
        }
    }
    setSkipConfirmationModal((prev) => ({
      ...prev,
      show: true,
      currentStep: step,
    }))
  }

  const skipToothSelectionMultiple = (
    procData?: any,
  ): Partial<IProcedureLogs>[] => {
    const currentProcedure = procData
    if (!currentProcedure) return [] as Partial<IProcedureLogs>[]

    const procedures: Partial<IProcedureLogs>[] = []

    return [...procedures, currentProcedure]
  }

  const setupProceduresData = async (): Promise<Partial<IProcedureLogs>[]> => {
    let bundledListData: any[] = []
    let updatedProcedures: Partial<IProcedureLogs>[] =
      currentAppt?.procedure_logs ?? []

    try {
      const response = await getBundledProcedures()
      if (response.statusCode === 200) {
        bundledListData = response.data?.bundled_procedures
      }
    } catch (e) {
      logger.error('Error fetching bundled procedures:', e)
    }

    if (bundledListData.length > 0) {
      const bundledProcedureItemIds = (currentAppt?.procedure_logs ?? [])
        .filter((proc) => proc.source === 2)
        .map((proc) => proc.source_id)
      const bundledProceduresData = bundledListData
        .filter((proc: any) =>
          proc.bundled_procedure_items.some((item: any) =>
            bundledProcedureItemIds.includes(item.bundled_procedure_item_id),
          ),
        )
        .map((proc: any) => ({
          ...proc,
        }))

      let bundledProcedures: any[] = []
      bundledProceduresData.forEach((proc: any) => {
        const newProcedureBundle: Partial<IProcedureLogs> = {
          proc_id: proc.bundled_procedure_id,
          proc_code: proc.bundled_code,
          proc_description: proc.bundled_name ?? '',
          proc_date: new Date(),
          provider_id: formData.providerId ?? '',
          patient_id: formData.patientId ?? '',
          tooth: null,
          surf: null,
          priority: 0,
          proc_status: 0,
          source: 2,
          id: proc.bundled_procedure_id,
          parentId: null,
        }

        bundledProcedures = [
          ...bundledProcedures,
          ...skipToothSelectionMultiple(newProcedureBundle),
        ]
      })

      bundledProcedures.forEach((bundledProcedure) => {
        updatedProcedures = updatedProcedures.map((proc) => {
          if (proc.source === 2 && proc.source_id != null) {
            const newBundledProceduresData = bundledListData.find((x: any) =>
              x.bundled_procedure_items?.some(
                (item: any) =>
                  item.bundled_procedure_item_id === proc.source_id,
              ),
            )
            if (newBundledProceduresData) {
              return {
                ...proc,
                ...(newBundledProceduresData
                  ? { parentId: newBundledProceduresData?.bundled_procedure_id }
                  : {}),
              }
            }
          }
          return proc
        })

        const childProcedures = updatedProcedures.filter(
          (p) => p.parentId === bundledProcedure.id,
        )

        const fieldsToSum = [
          'clinic_fee',
          'allowed_amount',
          'write_off',
          'estimated_insurance_pay',
          'estimated_patient_resp',
        ]

        fieldsToSum.forEach((field) => {
          bundledProcedure[field] = childProcedures.reduce(
            (acc, curr) =>
              acc + Number(curr[field as keyof IProcedureLogs] ?? 0),
            0,
          )
        })

        const index = updatedProcedures.findIndex(
          (p) => p.parentId === bundledProcedure.id,
        )

        updatedProcedures.splice(index, 0, bundledProcedure)
      })
    }

    return updatedProcedures ?? []
  }

  useEffect(() => {
    const updateFormData = async (): Promise<void> => {
      if (currentAppt) {
        const updatedProcedures = await setupProceduresData()
        const assignedFormIds: string[] =
          getAssignedFormTemplateIdsFromAppt(currentAppt)

        const identityKey =
          currentAppt.appointment_id != null &&
          String(currentAppt.appointment_id).trim() !== ''
            ? String(currentAppt.appointment_id)
            : null
        const normalizedIdentity = identityKey ?? 'NEW_APPT'
        const priorIdentity =
          apptNotesIdentityRef.current === '__UNSET__'
            ? '__UNSET__'
            : apptNotesIdentityRef.current
        const notesIdentityChanged = priorIdentity !== normalizedIdentity
        if (notesIdentityChanged) {
          apptNotesIdentityRef.current = normalizedIdentity
        }

        setFormData(
          (prev): AppointmentFormData => ({
            ...prev,
            patientName: currentAppt.patient
              ? getFullName(currentAppt.patient)
              : '',
            patientId: currentAppt.patient_id ?? '',
            providerName: currentAppt.provider
              ? getFullName(currentAppt.provider)
              : '',
            providerId: currentAppt.provider_id ?? '',
            operatoryName: '',
            operatoryId: currentAppt.operatory_id ?? '',
            duration: currentAppt.duration ?? 15,
            apptDate: moment(currentAppt?.appt_date ?? initialStartTime).format(
              'MM/DD/YYYY',
            ),
            apptStartTime: currentAppt?.appt_start ?? '',
            apptEndTime: currentAppt?.appt_end ?? '',
            appointmentTypeId: currentAppt.appointment_type_id ?? '',
            appointmentTypeName: currentAppt.appointmenttype?.name ?? '',
            isRecurring: false,
            recurringVisitId: currentAppt.recurring_visit_id ?? '',
            recurringEvery: currentAppt?.recurring_every ?? 0,
            recurringFor: currentAppt?.recurring_for ?? 1,
            recurringDays: currentAppt?.recurring_days ?? '0,1,2,3,4',
            recurringStartDate: currentAppt?.recurring_start_date
              ? moment(currentAppt.recurring_start_date).format('MM/DD/YYYY')
              : moment(initialStartTime).format('MM/DD/YYYY'),
            recurringEndDate: currentAppt?.recurring_end_date
              ? moment(currentAppt.recurring_end_date).format('MM/DD/YYYY')
              : moment(initialStartTime).add(7, 'days').format('MM/DD/YYYY'),
            isInsured: currentAppt.is_insured === true ? 'Yes' : 'No',
            procedures: updatedProcedures as IProcedureLogs[],
            insuranceName: '',
            insuranceId: currentAppt.insurance_plan_id ?? '',
            labOrders: currentAppt.lab_orders ?? [],
            templateName: currentAppt.template?.template_name ?? '',
            templateId: currentAppt.template?.template_id ?? '',
            files: currentAppt.files ?? [],
            // Same appointment: never replace with stale currentAppt.notes (often [] after
            // overlay sync + parent re-render). Identity change loads fresh from server.
            notes: notesIdentityChanged
              ? (currentAppt.notes ?? [])
              : (prev.notes ?? currentAppt.notes ?? []),
            contactVia: currentAppt.contact_via ?? [],
            assignedFormIds,
          }),
        )
      }
    }

    void updateFormData()
  }, [currentAppt, initialStartTime, initialEndTime])

  return (
    <>
      <Modal
        show={isOpen}
        onHide={onModalClose}
        size="lg"
        id={modalId}
        backdropClassName={backdropClassName}
        className={[
          'custom-modal-width',
          `appointment-step-${step}`,
          customClassName,
        ]
          .filter(Boolean)
          .join(' ')}
        backdrop="static"
        keyboard={false}
        enforceFocus={false}
      >
        <Modal.Header className="bg-custom3" closeButton>
          <Modal.Title className="fs-5 text-dark">
            Schedule appointment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {step !== 4 && <Stepper steps={steps} currentStep={step} />}
          {getSectionComponent()}
        </Modal.Body>

        <Modal.Footer className="d-inline-block">
          <Row md={12}>
            <Col md={4} className="text-start ps-0">
              {step > 0 && step < steps.length - 1 && (
                <Button size="md" className="button-link" onClick={handleSkip}>
                  Skip
                </Button>
              )}
            </Col>
            <Col md={8} className="text-end pe-0">
              {/* Step 0: Show "Cancel" only */}
              {step === 0 && (
                <>
                  <Button
                    size="md"
                    className="button-secondary left-button"
                    onClick={onModalClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="md"
                    className="button-primary"
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                </>
              )}
              {/* Steps between 1 and Review: Show "Previous" and "Next" */}
              {step > 0 && step < steps.length - 1 && (
                <>
                  <Button
                    size="md"
                    className="button-secondary left-button"
                    onClick={handlePrevious}
                  >
                    Previous
                  </Button>
                  <Button
                    size="md"
                    className="button-primary"
                    onClick={handleNext}
                    disabled={
                      (step === 1 &&
                        (!formData.procedures ||
                          formData.procedures.length === 0)) ||
                      (step === 2 &&
                        (!formData.labOrders ||
                          formData.labOrders.length === 0) &&
                        (!formData.notes || formData.notes.length === 0) &&
                        (!formData.templateId || formData.templateId === '') &&
                        (!formData.files || formData.files.length === 0) &&
                        (!formData.assignedFormIds ||
                          formData.assignedFormIds.length === 0) &&
                        !currentAppt)
                    }
                  >
                    Next
                  </Button>
                </>
              )}
              {/* Review step (step === steps.length - 1): Show "Previous" and "Confirm" */}
              {step === steps.length - 1 ? (
                <>
                  <Button
                    size="md"
                    className="button-secondary left-button"
                    onClick={handlePrevious}
                    disabled={loading.add}
                  >
                    Previous
                  </Button>
                  <Button
                    size="md"
                    className="button-primary"
                    onClick={handleSave}
                    disabled={loading.add}
                  >
                    Confirm
                  </Button>
                </>
              ) : null}
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
      <ConfirmationModal
        show={skipConfirmationModal.show}
        onHide={() => {
          setSkipConfirmationModal((prev) => ({ ...prev, show: false }))
        }}
        title={skipConfirmationModal.title}
        className="confirmation-modal--alert"
        headerClassName="confirmation-modal__header"
        size="md"
        body={skipConfirmationModal.body}
        footer={skipConfirmationModal.footer(step)}
      />

      <ConfirmationModal
        show={invalidFileAlert.show}
        onHide={() => setInvalidFileAlert({ show: false, fileName: '' })}
        title="Invalid file type"
        className="confirmation-modal--alert"
        headerClassName="confirmation-modal__header"
        size="md"
        body={
          <div className="confirmation-modal__body">
            <p className="confirmation-modal__title">File not supported</p>
            <p className="confirmation-modal__message">
              {invalidFileAlert.fileName
                ? `Invalid file type: ${invalidFileAlert.fileName}. Only .docx and .pdf files are allowed.`
                : 'Only .docx and .pdf files are allowed.'}
            </p>
          </div>
        }
        footer={
          <div className="confirmation-modal__footer">
            <Button
              className="button-primary"
              size="md"
              onClick={() => setInvalidFileAlert({ show: false, fileName: '' })}
            >
              Got it
            </Button>
          </div>
        }
      />
    </>
  )
}

export default Appointment
